<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  execution: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const apiResultLabel = result => {
  const labels = {
    success: t('CORTEX.PLAYGROUND.EXECUTION.API_RESULT.SUCCESS'),
    payment_required: t(
      'CORTEX.PLAYGROUND.EXECUTION.API_RESULT.PAYMENT_REQUIRED'
    ),
    rate_limited: t('CORTEX.PLAYGROUND.EXECUTION.API_RESULT.RATE_LIMITED'),
    authentication_failed: t(
      'CORTEX.PLAYGROUND.EXECUTION.API_RESULT.AUTHENTICATION_FAILED'
    ),
    timeout: t('CORTEX.PLAYGROUND.EXECUTION.API_RESULT.TIMEOUT'),
    request_failed: t('CORTEX.PLAYGROUND.EXECUTION.API_RESULT.REQUEST_FAILED'),
    llm_unavailable: t(
      'CORTEX.PLAYGROUND.EXECUTION.API_RESULT.LLM_UNAVAILABLE'
    ),
    knowledge_not_found: t(
      'CORTEX.PLAYGROUND.EXECUTION.API_RESULT.KNOWLEDGE_NOT_FOUND'
    ),
    not_recorded: t('CORTEX.PLAYGROUND.EXECUTION.API_RESULT.NOT_RECORDED'),
  };

  return labels[result] || labels.not_recorded;
};

const statusColor = result => {
  if (result === 'success') return 'teal';
  if (result === 'payment_required') return 'amber';
  if (result === 'rate_limited') return 'amber';

  return result ? 'ruby' : 'slate';
};

const formatCost = usage => {
  const costMicros = Number(usage?.cost_micros);
  const currency = usage?.currency || 'USD';
  if (!Number.isFinite(costMicros)) {
    return t('CORTEX.PLAYGROUND.EXECUTION.NOT_AVAILABLE');
  }

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  }).format(costMicros / 1_000_000);
};

const attempts = computed(() => props.execution?.attempts || []);
</script>

<template>
  <section class="grid gap-3 pt-4 border-t border-n-weak">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('CORTEX.PLAYGROUND.EXECUTION.TITLE') }}
      </h3>
      <Label
        :label="apiResultLabel(execution.api_result)"
        :color="statusColor(execution.api_result)"
        compact
      />
    </div>
    <dl class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <div>
        <dt class="text-xs text-n-slate-10">
          {{ t('CORTEX.PLAYGROUND.EXECUTION.MODEL') }}
        </dt>
        <dd class="mt-1 text-sm font-medium break-all text-n-slate-12">
          {{
            execution.model || t('CORTEX.PLAYGROUND.EXECUTION.NOT_AVAILABLE')
          }}
        </dd>
      </div>
      <div>
        <dt class="text-xs text-n-slate-10">
          {{ t('CORTEX.PLAYGROUND.EXECUTION.PROVIDER') }}
        </dt>
        <dd class="mt-1 text-sm font-medium text-n-slate-12">
          {{
            execution.provider || t('CORTEX.PLAYGROUND.EXECUTION.NOT_AVAILABLE')
          }}
        </dd>
      </div>
      <div>
        <dt class="text-xs text-n-slate-10">
          {{ t('CORTEX.PLAYGROUND.EXECUTION.API_RETURN') }}
        </dt>
        <dd class="mt-1 text-sm font-medium text-n-slate-12">
          {{ apiResultLabel(execution.api_result) }}
        </dd>
      </div>
      <div>
        <dt class="text-xs text-n-slate-10">
          {{ t('CORTEX.PLAYGROUND.EXECUTION.COST') }}
        </dt>
        <dd class="mt-1 text-sm font-medium text-n-slate-12">
          {{ formatCost(execution.usage) }}
        </dd>
      </div>
    </dl>
    <div v-if="attempts.length" class="grid gap-2">
      <p class="text-xs font-medium text-n-slate-11">
        {{ t('CORTEX.PLAYGROUND.EXECUTION.ATTEMPTS') }}
      </p>
      <div
        v-for="(attempt, index) in attempts"
        :key="`${attempt.provider}-${attempt.model}-${index}`"
        class="grid gap-1 pt-2 text-xs border-t border-n-weak sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-3"
      >
        <span class="font-medium text-n-slate-11">
          {{ t('CORTEX.PLAYGROUND.EXECUTION.ATTEMPT', { number: index + 1 }) }}
        </span>
        <span class="break-all text-n-slate-12">
          {{
            t('CORTEX.PLAYGROUND.EXECUTION.TARGET', {
              provider: attempt.provider,
              model: attempt.model,
            })
          }}
        </span>
        <span class="text-n-slate-10">
          {{ apiResultLabel(attempt.api_result) }}
          <template v-if="Number.isFinite(Number(attempt.duration_ms))">
            {{
              t('CORTEX.PLAYGROUND.EXECUTION.DURATION', {
                duration: attempt.duration_ms,
              })
            }}
          </template>
        </span>
      </div>
    </div>
  </section>
</template>
